import {DateRangeInput} from '@components/common/date-picker/date-range-input'
import {DropdownList} from '@components/common/dropdown-list/dropdown-list'
import {DivWithHint} from '@components/common/hint/hint'
import {Icon} from '@components/common/icon/icon'
import {Option} from '@components/common/select/select'
import {TriCheckbox} from '@components/common/tri-checkbox/tri-checkbox'
import {Remove, Separator} from '@components/statistics/stats-reports/common'
import React, {useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {ReportDateFilter} from 'yougile-model'
import {
  RelativeTime,
  relativeTimeToTimestamp,
  Time,
  TimeUnit
} from 'yougile-util'

interface OuterProps {
  filter: ReportDateFilter
  isDeadline?: boolean
  canBeInFuture?: boolean
  isAny?: boolean
  dontOpen?: boolean
  showRemoveButton?: boolean
  onOpenChange?: (isOpen: boolean) => void
  placeholder?: string
  classNameWidth?: string
  moveDates?: boolean
}

interface Props extends OuterProps {
  update: (filters: ReportDateFilter) => void
}

const timeToStr = (t: RelativeTime) => (t ? `${t.value}${t.unit}` : '')
const filterToStr = (f: ReportDateFilter) =>
  `${f.custom ? 'C' : 'D'}:${timeToStr(f.start)}-${timeToStr(f.end)}`

const makeFilter = (
  unit: TimeUnit,
  start: number,
  end: number,
  datesFrom?: ReportDateFilter['datesFrom']
): ReportDateFilter => ({
  start: {unit, value: start},
  end: {unit, value: end},
  datesFrom: datesFrom ?? 'now'
})

type DateRangeOption =
  | 'any'
  | 'custom'
  | 'day'
  | '7day'
  | '30day'
  | 'currentWeek'
  | 'currentMonth'
  | 'prevWeek'
  | 'prevMonth'
  | 'next7day'
  | 'next30day'
  | 'nextWeek'
  | 'nextMonth'
  | 'expired'
  | 'not-expired'
  | 'set'
  | 'not-set'

const optionToFilter: Map<DateRangeOption, ReportDateFilter> = new Map()
optionToFilter.set('day', makeFilter('day', 0, 1))
optionToFilter.set('7day', makeFilter('day', -7, 1))
optionToFilter.set('30day', makeFilter('day', -30, 1))
optionToFilter.set('next7day', makeFilter('day', 0, 7))
optionToFilter.set('next30day', makeFilter('day', 0, 30))
optionToFilter.set('currentWeek', makeFilter('week', 0, 1))
optionToFilter.set('currentMonth', makeFilter('month', 0, 1))
optionToFilter.set('prevWeek', makeFilter('week', -1, 0))
optionToFilter.set('prevMonth', makeFilter('month', -1, 0))
optionToFilter.set('nextWeek', makeFilter('week', 1, 2))
optionToFilter.set('nextMonth', makeFilter('month', 1, 2))
optionToFilter.set('expired', {type: 'expired', ...makeFilter('day', 0, 0)})
optionToFilter.set('not-expired', {
  type: 'not-expired',
  ...makeFilter('day', 0, 0)
})
optionToFilter.set('set', {type: 'set', ...makeFilter('day', 0, 0)})
optionToFilter.set('not-set', {type: 'not-set', ...makeFilter('day', 0, 0)})

const filterToOption: Map<string, DateRangeOption> = new Map(
  [...optionToFilter].map(
    v => [filterToStr(v[1]), v[0]] as [string, DateRangeOption]
  )
)

// eslint-disable-next-line max-lines-per-function
export const DateFilter = (props: Props) => {
  const {
    filter,
    update,
    canBeInFuture = false,
    dontOpen = false,
    showRemoveButton = true,
    onOpenChange,
    classNameWidth,
    moveDates = true
  } = props

  const opts = getOpts(props)
  const selectValue = getSelectedValue(props)

  const change = (newVal: DateRangeOption) => {
    if (newVal === 'custom') {
      update({...makeFilter('day', 0, 1), custom: true})
    } else {
      update(optionToFilter.get(newVal))
    }
  }
  const day = 24 * 3600_000

  const onDatesChoose = (range: [Date, Date]) => {
    const today = Time.middleOfDay(Time.now())
    const d1 = Time.toTimestamp(range[0])
    const d2 = Time.toTimestamp(range[1])
    const start = Math.round((Time.middleOfDay(d1) - today) / day)
    const end = Math.round(Time.middleOfDay(d2 - today) / day) + 1
    update({...makeFilter('day', start, end, filter.datesFrom), custom: true})
  }

  const dates: [Date, Date] = filter?.custom
    ? [
        Time.toLocalDate(
          relativeTimeToTimestamp(filter.start, filter.datesFrom) + 0.5 * day
        ),
        Time.toLocalDate(
          relativeTimeToTimestamp(filter.end, filter.datesFrom) - 0.5 * day
        )
      ]
    : undefined

  const toggleRelativeDates = () => {
    if (!filter || !filter.custom) {
      return
    }
    const datesFrom = filter.datesFrom ?? 'now'
    if (datesFrom === 'now') {
      update({...filter, datesFrom: Time.now()})
    } else {
      const d1 = relativeTimeToTimestamp(filter.start, filter.datesFrom)
      const d2 = relativeTimeToTimestamp(filter.end, filter.datesFrom)
      const today = Time.middleOfDay(Time.now())
      const start = Math.round((Time.middleOfDay(d1) - today) / day)
      const end = Math.round(Time.middleOfDay(d2 - today) / day) + 1
      update({...makeFilter('day', start, end, 'now'), custom: true})
    }
  }

  useEffect(() => {
    if (!props.filter) {
      props.update(optionToFilter.get(opts[0].value))
    }
  }, [props.filter])

  return (
    <div className="flex justify-between">
      <div className="flex items-center">
        <DropdownList
          className={classNameWidth ?? 'w-160'}
          initialOpen={!dontOpen && !props.filter}
          options={opts}
          value={selectValue}
          onChange={change}
          onOpenChange={isOpen => {
            if (typeof onOpenChange === 'function') {
              onOpenChange(isOpen)
            }
          }}
        />
        {dates && (
          <>
            <Separator />
            <DateRangeInput
              initialDates={dates}
              onChoose={onDatesChoose}
              disableFutureDates={!canBeInFuture}
              onOpenChange={isOpen => {
                if (typeof onOpenChange === 'function') {
                  onOpenChange(isOpen)
                }
              }}
            />
            {moveDates && (
              <>
                <TriCheckbox
                  className="ml-8 mr-4"
                  onChange={toggleRelativeDates}
                  value={(filter.datesFrom ?? 'now') === 'now'}
                />
                <div
                  className="mr-4 cursor-pointer text-12"
                  onClick={toggleRelativeDates}
                >
                  {Lang.msg.report.moveDates}
                </div>
                <DivWithHint
                  className="w-16 h-16 cursor-help"
                  content={Lang.msg.report.moveDatesHint}
                >
                  <Icon className="w-16 h-16" name="main-menu-help" />
                </DivWithHint>
              </>
            )}
          </>
        )}
      </div>
      {showRemoveButton && <Remove update={update} />}
    </div>
  )
}

const getSelectedValue = (props: OuterProps) => {
  const {filter} = props
  const selectValue = filter
    ? filter.type
      ? filter.type
      : filterToOption.has(filterToStr(filter))
      ? filterToOption.get(filterToStr(filter))
      : 'custom'
    : 'any'

  return selectValue
}

const getOpts = (props: OuterProps) => {
  const {
    canBeInFuture = false,
    isDeadline = false,
    isAny = false,
    placeholder
  } = props

  const opts: Array<Option & {value: DateRangeOption}> = [
    {value: 'day', label: Lang.msg.statsFilterDate1d},
    {value: '7day', label: Lang.msg.statsFilter7d},
    {value: '30day', label: Lang.msg.statsFilter30d},
    {value: 'currentWeek', label: Lang.msg.statsFilterCurrentWeek},
    {value: 'currentMonth', label: Lang.msg.statsFilterCurrentMonth},
    {value: 'prevWeek', label: Lang.msg.statsFilterPrevWeek},
    {value: 'prevMonth', label: Lang.msg.statsFilterPrevMonth}
  ]
  if (isAny) {
    const anyOpts: Array<Option & {value: DateRangeOption}> = [
      {value: 'any', label: placeholder ?? Lang.msg.statsFilterAny}
    ]
    opts.splice(0, 0, ...anyOpts)
  }
  if (isDeadline) {
    const deadlineOpts: Array<Option & {value: DateRangeOption}> = [
      {value: 'set', label: Lang.msg.component.dateFilter.set},
      {
        value: 'not-set',
        label: Lang.msg.component.dateFilter.notSet
      },
      {value: 'expired', label: Lang.msg.component.dateFilter.expired},
      {value: 'not-expired', label: Lang.msg.component.dateFilter.notExpired}
    ]
    opts.splice(0, 0, ...deadlineOpts)
  }
  if (canBeInFuture) {
    opts.push({value: 'next7day', label: Lang.msg.statsFilterNext7d})
    opts.push({value: 'next30day', label: Lang.msg.statsFilterNext30d})
    opts.push({value: 'nextWeek', label: Lang.msg.statsFilterNextWeek})
    opts.push({value: 'nextMonth', label: Lang.msg.statsFilterNextMonth})
  }
  opts.push({
    value: 'custom',
    label:
      Lang.msg.timeCustom + (getSelectedValue(props) !== 'custom' ? '…' : '')
  })
  return opts
}

export const DateFilterValue = (props: OuterProps) => {
  const opts = getOpts(props)
  const selectValue = getSelectedValue(props)
  const value = opts.find(opt => opt.value === selectValue)
  return <>{value?.label ?? ''}</>
}
